import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import SearchableDropdown, { DropdownOption } from '../../../_common/SearchableDropdown/SearchableDropdown';
import {
    GET_ALL_ASSET_LOAD_TYPES,
    GET_ALL_REGIONS,
    GET_ALL_TRAILER_TYPES,
    GET_ALL_TRAILERS,
    GET_LOCATION_INFO,
    GET_DRIVERS_BY_REGION,
    GET_ALL_LOCATIONS,
} from '../../../../graphql/client/queries';
import { useQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    GetAssestLoadsByLoadId_getAssestLoadsByLoadId as AssetLoadType,
    GetAllAssetLoadTypes,
    GetAllLocations,
    GetAllRegions,
    GetAllTrailerTypes,
    GetAllTrailers,
    GetDriversByRegion,
    GetLocationInfo,
} from '../../../../types/schemaTypes';
import { blockInvalidChar } from '../../../_common/blockInvalidChar';
import { useAuthContext } from '../../../Auth/AuthProvider';
import { PermissionEnum } from '../../../../types/PermissionEnum';

interface LoadDriverDetailsEditProps {
    assetInfoExists: boolean;
    assetLoadToSave: AssetLoadType | undefined;
    setAssetLoadToSave: React.Dispatch<React.SetStateAction<any>>;
    payCodeDropdownOptions: DropdownOption[];
    selectedTrailer: string;
}

const LoadDriverDetailsEdit = ({
    assetInfoExists,
    assetLoadToSave,
    setAssetLoadToSave,
    payCodeDropdownOptions,
    selectedTrailer,
}: LoadDriverDetailsEditProps): React.ReactElement => {
    const isEditLoadEnabled = useAuthContext().userHasPermission(PermissionEnum.EditAssetLoadDetails);
    let isAssignLoadEnabled = useAuthContext().userHasPermission(PermissionEnum.AssignAssetLoads);

    // Driver section edit should be disabled if status is loading and above
    if (!["Created", "ReadyForDispatch", "Assigned"].includes(assetLoadToSave?.status ?? ''))
        isAssignLoadEnabled = false;

    const searchIcon = <FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'search' }} />;

    const loadTypeDropdownOptions: DropdownOption[] = [];
    const { data: assetLoadTypes } = useQuery<GetAllAssetLoadTypes>(GET_ALL_ASSET_LOAD_TYPES, {
        skip: !isEditLoadEnabled,
    });
    if (assetLoadTypes?.getAllAssetLoadTypes) {
        (assetLoadTypes.getAllAssetLoadTypes ?? []).forEach((item) => {
            if (item != null) loadTypeDropdownOptions.push({ id: item.id, name: item.name });
        });
    }

    const originDestinationTypeDropdownOptions: DropdownOption[] = [{ id: 1, name: 'Job' }, { id: 2, name: 'Lofa' }, { id: 3, name: 'Yard' }, { id: 4, name: 'Other' }];

    const originDropdownOptions: DropdownOption[] = [];
    const destinationDropdownOptions: DropdownOption[] = [];

    const latErrorMessage = 'The valid range is from -90 to 90.';
    const lngErrorMessage = 'The valid range is from -180 to 180.';
    const [isOriginLatError, setIsOriginLatError] = useState(false);
    const [isOriginLngError, setIsOriginLngError] = useState(false);
    const [isDestLatError, setIsDestLatError] = useState(false);
    const [isDestLngError, setIsDestLngError] = useState(false);

    // If Manual -- Send location ID as 0 when Origin is not "Other" to get the Origin dropdown
    const { data: originData } = useQuery<GetAllLocations>(GET_ALL_LOCATIONS, {
        variables: {
            input: {
                locationType: assetLoadToSave?.originType,
                locationId: 0,
            }
        },
        fetchPolicy: 'network-only',
        skip: !isEditLoadEnabled || !assetLoadToSave?.originType || assetLoadToSave.originType === "Other",
    });

    // If Manual -- Send Origin ID as Location ID. Send Location ID as 0 if Origin is "Other" or Load type is Transfer/ Move to get the Destination dropdown
    const { data: destinationData } = useQuery<GetAllLocations>(GET_ALL_LOCATIONS, {
        variables: {
            input: {
                locationType: assetLoadToSave?.originType,
                locationId: ["Equipment Transfer", "Equipment Move"].includes(assetLoadToSave?.assetLoadType ?? '') ? 0 : (
                    assetLoadToSave?.originType === "Job" ? assetLoadToSave.originJobId : (
                        assetLoadToSave?.originType === "Lofa" ? assetLoadToSave.originLofaId : (
                            assetLoadToSave?.originType === "Yard" ? assetLoadToSave.originYardId : (
                                0
                            )
                        )
                    )
                ),
            }
        },
        fetchPolicy: 'network-only',
        skip: !isEditLoadEnabled || !assetLoadToSave?.destinationType || !assetLoadToSave?.originType ||
            (assetLoadToSave.originType === "Job" && !assetLoadToSave.originJobId) ||
            (assetLoadToSave.originType === "Lofa" && !assetLoadToSave.originLofaId) ||
            (assetLoadToSave.originType === "Yard" && !assetLoadToSave.originYardId),
    });

    if (originData && originData.getAllLocations) {
        originData.getAllLocations.map(item => {
            if (item && item.locationType === assetLoadToSave?.originType && item.locationId && item.descrip) {
                originDropdownOptions.push({
                    id: item.locationId,
                    name: item.descrip,
                })
            }
        })
    }

    if (destinationData && destinationData.getAllLocations) {
        destinationData.getAllLocations.map(item => {
            if (item && item.locationType === assetLoadToSave?.destinationType && item.locationId && item.descrip) {
                destinationDropdownOptions.push({
                    id: item.locationId,
                    name: item.descrip,
                })
            }
        })
    }

    const { data: locationInfoData } = useQuery<GetLocationInfo>(GET_LOCATION_INFO, {
        variables: {
            input: {
                originId: assetLoadToSave?.originType === "Job" ?
                    (
                        assetLoadToSave?.originJobId ?? -1
                    ) : (
                        assetLoadToSave?.originType === "Lofa" ?
                            (
                                assetLoadToSave?.originLofaId ?? -1
                            ) : (
                                assetLoadToSave?.originType === "Yard" ?
                                    (
                                        assetLoadToSave?.originYardId ?? -1
                                    ) : (
                                        -1
                                    )
                            )
                    ),
                originType: assetLoadToSave?.originType?.length === 0 ? "-1" : assetLoadToSave?.originType,
                destinationId: assetLoadToSave?.destinationType === "Job" ?
                    (
                        assetLoadToSave?.destinationJobId ?? -1
                    ) : (
                        assetLoadToSave?.destinationType === "Lofa" ?
                            (
                                assetLoadToSave?.destinationLofaId ?? -1
                            ) : (
                                assetLoadToSave?.destinationType === "Yard" ?
                                    (
                                        assetLoadToSave?.destinationYardId ?? -1
                                    ) : (
                                        -1
                                    )
                            )
                    ),
                destinationType: assetLoadToSave?.destinationType?.length === 0 ? "-1" : assetLoadToSave?.destinationType,
            }
        },
        fetchPolicy: "network-only",
        skip: (assetLoadToSave && assetLoadToSave?.originType?.length !== 0 && assetLoadToSave?.originType !== "Other" && !assetLoadToSave.originJobId && !assetLoadToSave.originLofaId && !assetLoadToSave.originYardId) ||
            (assetLoadToSave && assetLoadToSave?.destinationType?.length !== 0 && assetLoadToSave?.destinationType !== "Other" && !assetLoadToSave.destinationJobId && !assetLoadToSave.destinationLofaId && !assetLoadToSave.destinationYardId)
    });

    useEffect(() => {
        if (!locationInfoData?.getLocationInfo) return;
        const values = locationInfoData.getLocationInfo;
        setAssetLoadToSave((prev) => {
            const temp = { ...prev };
            if (values.originRegionId != null) {
                temp.originRegionId = values.originRegionId;
                temp.originRegion = values.originRegionDescrip;
            }
            if (values.originDistrictId != null) {
                temp.originDistrictId = values.originDistrictId;
                temp.originDistrict = values.originDistrictDescrip;
            }
            if (values.destinationRegionId != null) {
                temp.destinationRegionId = values.destinationRegionId;
                temp.destinationRegion = values.destinationRegionDescrip;
            }
            if (values.destinationDistrictId != null) {
                temp.destinationDistrictId = values.destinationDistrictId;
                temp.destinationDistrict = values.destinationDistrictDescrip;
            }
            return temp;
        });
    }, [locationInfoData, setAssetLoadToSave]);

    const regionDropdownOptions: DropdownOption[] = [];
    const { data: regions } = useQuery<GetAllRegions>(GET_ALL_REGIONS, {
        skip: !isEditLoadEnabled,
    });
    if (regions?.getAllRegions) {
        (regions.getAllRegions ?? []).forEach((item) => {
            if (item == null) return;
            // If Equipment transfer then remove origin region from the list
            if (assetLoadToSave?.assetLoadType !== "Equipment Transfer" || item.id !== assetLoadToSave.originRegionId)
                regionDropdownOptions.push({
                    id: item.id,
                    name: item.regionName ?? '',
                });
        });
    }

    const originDistrictDropdownOptions: DropdownOption[] = [];
    if (assetLoadToSave?.originRegionId && regions?.getAllRegions) {
        const selectedRegion = (regions.getAllRegions ?? []).filter((r) => r != null && r.id === assetLoadToSave?.originRegionId)[0];
        (selectedRegion?.districts ?? []).forEach((item) => {
            if (item?.id != null && item?.districtName != null) {
                originDistrictDropdownOptions.push({
                    id: item.id,
                    name: item.districtName,
                });
            }
        });
    }

    const destinationDistrictDropdownOptions: DropdownOption[] = [];
    if (assetLoadToSave?.destinationRegionId && regions?.getAllRegions) {
        const selectedRegion = (regions.getAllRegions ?? []).filter((r) => r != null && r.id === assetLoadToSave?.destinationRegionId)[0];
        (selectedRegion?.districts ?? []).forEach((item) => {
            if (item?.id != null && item?.districtName != null) {
                destinationDistrictDropdownOptions.push({
                    id: item.id,
                    name: item.districtName,
                });
            }
        });
    }

    const trailerTypeDropdownOptions: DropdownOption[] = [];
    const { data: trailerTypes } = useQuery<GetAllTrailerTypes>(GET_ALL_TRAILER_TYPES, {
        skip: !isEditLoadEnabled,
    });
    if (trailerTypes?.getAllTrailerTypes) {
        (trailerTypes.getAllTrailerTypes ?? []).forEach((item) => {
            if (item != null) trailerTypeDropdownOptions.push({ id: item.id, name: item.name });
        });
    }

    const driverDropdownOptions: DropdownOption[] = [];
    const { data: drivers } = useQuery<GetDriversByRegion>(GET_DRIVERS_BY_REGION, {
        variables: {
            input: {
                regionId: assetLoadToSave?.originRegionId ?? 0,
                districtId: assetLoadToSave?.originDistrictId ?? 0
            }
        },
        fetchPolicy: "network-only",
        skip: !isAssignLoadEnabled,
    });
    if (drivers && drivers.getDriversByRegion) {
        drivers.getDriversByRegion.map(item => {
            if (item && item.driverName && item.id)
                driverDropdownOptions.push({
                    id: item.id,
                    name: item.driverName,
                })
        })
    }

    const trailerDropdownOptions: DropdownOption[] = [];
    const { data: trailerData, loading: trailerDataLoading } = useQuery<GetAllTrailers>(GET_ALL_TRAILERS, {
        variables: {
            input: {
                trailerTypeId: assetLoadToSave?.trailerTypeId,
                assetLoadId: assetLoadToSave?.id,
            }
        },
        fetchPolicy: "network-only",
        skip: !assetLoadToSave?.trailerTypeId
    });
    if (trailerData?.getAllTrailers) {
        (trailerData.getAllTrailers ?? []).forEach((item) => {
            if (item != null) trailerDropdownOptions.push({ id: item.id, name: item.name });
        });
    }

    const updateAssetLoad = (inputTypes: string[], values: any[]) => {
        const tempAssetLoadToSave = { ...assetLoadToSave };
        inputTypes.map((inputType, index) => {
            tempAssetLoadToSave[inputType] = values[index];
        })
        setAssetLoadToSave(tempAssetLoadToSave);
    }

    const handleOnChangeLoadType = (assetLoadTypeId: number) => {
        if (!isEditLoadEnabled) return;

        let assetLoadType = '';
        loadTypeDropdownOptions.map(item => {
            if (item.id === assetLoadTypeId)
                assetLoadType = item.name.toString()
        })

        // If 'Equipment Transfer' then reset Destination
        if (assetLoadType === "Equipment Transfer")
            updateAssetLoad(
                ['assetLoadTypeId', 'assetLoadType', 'destinationType', 'destinationJobId', 'destinationJob', 'destinationLofaId', 'destinationLofa', 'destinationYardId', 'destinationYard', 'destinationLongitude', 'destinationLatitude', 'destinationRegionId', 'destinationRegion', 'destinationDistrictId', 'destinationDistrict'],
                [assetLoadTypeId, assetLoadType, null, null, null, null, null, null, null, null, null, null, null, null, null]
            )
        // If 'Equipment Move' then reset Destination with Same region as Origin
        else if (assetLoadType === "Equipment Move")
            updateAssetLoad(
                ['assetLoadTypeId', 'assetLoadType', 'destinationType', 'destinationJobId', 'destinationJob', 'destinationLofaId', 'destinationLofa', 'destinationYardId', 'destinationYard', 'destinationLongitude', 'destinationLatitude', 'destinationRegionId', 'destinationRegion', 'destinationDistrictId', 'destinationDistrict'],
                [assetLoadTypeId, assetLoadType, null, null, null, null, null, null, null, null, null, assetLoadToSave?.originRegionId, assetLoadToSave?.originRegion, null, null]
            )
        else
            updateAssetLoad(
                ['assetLoadTypeId', 'assetLoadType'],
                [assetLoadTypeId, assetLoadType]
            )
    }

    const handleOnChangeTerminalType = (value: number, isOrigin: boolean) => {
        if (!isEditLoadEnabled) return;

        let terminalType = '';
        if (isOrigin) {
            originDestinationTypeDropdownOptions.map(item => {
                if (item.id === value)
                    terminalType = item.name.toString();
            })
            updateAssetLoad(
                ['originType', 'originJobId', 'originJob', 'originLofaId', 'originLofa', 'originYardId', 'originYard', 'originLongitude', 'originLatitude', 'originRegionId', 'originRegion', 'originDistrictId', 'originDistrict'],
                [terminalType, null, null, null, null, null, null, null, null, null, null, null, null]
            );
        }
        else {
            originDestinationTypeDropdownOptions.map(item => {
                if (item.id === value)
                    terminalType = item.name.toString();
            })
            updateAssetLoad(
                ['destinationType', 'destinationJobId', 'destinationJob', 'destinationLofaId', 'destinationLofa', 'destinationYardId', 'destinationYard', 'destinationLongitude', 'destinationLatitude', 'destinationRegionId', 'destinationRegion', 'destinationDistrictId', 'destinationDistrict'],
                [terminalType, null, null, null, null, null, null, null, null, null, null, null, null]
            );
        }
    }

    const handleOnChangeTerminal = (terminalId: number | null, isOrigin: boolean) => {
        if (!isEditLoadEnabled) return;

        let terminal = '';
        if (terminalId === 0) terminalId = null;
        if (isOrigin) {
            originDropdownOptions.map(item => {
                if (item.id === terminalId)
                    terminal = item.name.toString()
            })

            if (assetLoadToSave?.originType === 'Job')
                updateAssetLoad(
                    ['originJobId', 'originJob', 'originLofaId', 'originLofa', 'originYardId', 'originYard', 'originLongitude', 'originLatitude'],
                    [terminalId, terminal, null, null, null, null, null, null, null,]
                );
            if (assetLoadToSave?.originType === 'Lofa')
                updateAssetLoad(
                    ['originJobId', 'originJob', 'originLofaId', 'originLofa', 'originYardId', 'originYard', 'originLongitude', 'originLatitude'],
                    [null, null, terminalId, terminal, null, null, null, null, null,]
                );
            if (assetLoadToSave?.originType === 'Yard')
                updateAssetLoad(
                    ['originJobId', 'originJob', 'originLofaId', 'originLofa', 'originYardId', 'originYard', 'originLongitude', 'originLatitude'],
                    [null, null, null, null, terminalId, terminal, null, null, null,]
                );
        }
        else {
            destinationDropdownOptions.map(item => {
                if (item.id === terminalId)
                    terminal = item.name.toString();
            })

            if (assetLoadToSave?.destinationType === 'Job')
                updateAssetLoad(
                    ['destinationJobId', 'destinationJob', 'destinationLofaId', 'destinationLofa', 'destinationYardId', 'destinationYard', 'destinationLongitude', 'destinationLatitude'],
                    [terminalId, terminal, null, null, null, null, null, null, null,]
                );
            if (assetLoadToSave?.destinationType === 'Lofa')
                updateAssetLoad(
                    ['destinationJobId', 'destinationJob', 'destinationLofaId', 'destinationLofa', 'destinationYardId', 'destinationYard', 'destinationLongitude', 'destinationLatitude'],
                    [null, null, terminalId, terminal, null, null, null, null, null,]
                );
            if (assetLoadToSave?.destinationType === 'Yard')
                updateAssetLoad(
                    ['destinationJobId', 'destinationJob', 'destinationLofaId', 'destinationLofa', 'destinationYardId', 'destinationYard', 'destinationLongitude', 'destinationLatitude'],
                    [null, null, null, null, terminalId, terminal, null, null, null,]
                );
        }
    }

    const handleOnChangeOriginLat = (value: string) => {
        if (!isEditLoadEnabled) return;

        const lat = parseFloat(value);
        if (lat < -90 || lat > 90) setIsOriginLatError(true);
        else setIsOriginLatError(false);
        if (Number.isNaN(lat))
            updateAssetLoad(['originLatitude'], [null]);
        else
            updateAssetLoad(['originLatitude'], [lat]);
    }

    const handleOnChangeOriginLong = (value: string) => {
        if (!isEditLoadEnabled) return;

        const long = parseFloat(value);
        if (long < -180 || long > 180) setIsOriginLngError(true);
        else setIsOriginLngError(false);
        if (Number.isNaN(long))
            updateAssetLoad(['originLongitude'], [null]);
        else
            updateAssetLoad(['originLongitude'], [long]);
    }

    const handleOnChangeDestinationLat = (value: string) => {
        if (!isEditLoadEnabled) return;

        const lat = parseFloat(value);
        if (lat < -90 || lat > 90) setIsDestLatError(true);
        else setIsDestLatError(false);
        if (Number.isNaN(lat))
            updateAssetLoad(['destinationLatitude'], [null]);
        else
            updateAssetLoad(['destinationLatitude'], [lat]);
    }

    const handleOnChangeDestinationLong = (value: string) => {
        if (!isEditLoadEnabled) return;

        const long = parseFloat(value);
        if (long < -180 || long > 180) setIsDestLngError(true);
        else setIsDestLngError(false);
        if (Number.isNaN(long))
            updateAssetLoad(['destinationLongitude'], [null]);
        else
            updateAssetLoad(['destinationLongitude'], [long]);
    }

    const handleOnChangeRegion = (regionId: number, isOrigin: boolean) => {
        if (!isEditLoadEnabled) return;

        let region = '';
        if (isOrigin) {
            regionDropdownOptions.map(item => {
                if (item.id === regionId)
                    region = item.name.toString();
            })
            updateAssetLoad(
                ['originRegionId', 'originRegion', 'originDistrictId', 'originDistrict'],
                [regionId, region, null, null]
            );
        }
        else {
            regionDropdownOptions.map(item => {
                if (item.id === regionId)
                    region = item.name.toString();
            })
            updateAssetLoad(
                ['destinationRegionId', 'destinationRegion', 'destinationDistrictId', 'destinationDistrict'],
                [regionId, region, null, null]
            );
        }
    }

    const handleOnChangeDistrict = (districtId: number, isOrigin: boolean) => {
        if (!isEditLoadEnabled) return;

        let district = '';
        if (isOrigin) {
            originDistrictDropdownOptions.map(item => {
                if (item.id === districtId)
                    district = item.name.toString();
            })
            updateAssetLoad(
                ['originDistrictId', 'originDistrict'],
                [districtId, district]
            );
        }
        else {
            destinationDistrictDropdownOptions.map(item => {
                if (item.id === districtId)
                    district = item.name.toString();
            })
            updateAssetLoad(
                ['destinationDistrictId', 'destinationDistrict'],
                [districtId, district]
            );
        }
    }

    const handleOnChangeTrailerType = (trailerTypeId: number | null) => {
        if (!isEditLoadEnabled) return;

        if (trailerTypeId === 0) trailerTypeId = null;
        let trailerType = '';
        trailerTypeDropdownOptions.map(item => {
            if (item.id === trailerTypeId)
                trailerType = item.name.toString();
        })

        // If Trailer type is 'Stepdeck' then Paycode must be 'Stepdeck'
        if (trailerType === 'Stepdeck') {
            updateAssetLoad(
                ['trailerTypeId', 'trailerType', 'trailerId', 'trailer', 'payCode'],
                [trailerTypeId, trailerType, null, null, 'Stepdeck']
            );
        }
        // If Trailer type is 'Chassis' then Paycode must be 'Single Empty Box'
        else if (trailerType === 'Sandbox Chassis') {
            updateAssetLoad(
                ['trailerTypeId', 'trailerType', 'trailerId', 'trailer', 'payCode'],
                [trailerTypeId, trailerType, null, null, 'Single Empty Box']
            );
        }
        // If Trailer type is 'Cradle' then Paycode must be 'Cradle'
        else if (trailerType === 'Cradle') {
            updateAssetLoad(
                ['trailerTypeId', 'trailerType', 'trailerId', 'trailer', 'payCode'],
                [trailerTypeId, trailerType, null, null, 'Cradle']
            );
        }
        else {
            updateAssetLoad(
                ['trailerTypeId', 'trailerType', 'trailerId', 'trailer'],
                [trailerTypeId, trailerType, null, null]
            );
        }
    }

    const handleOnChangePayCode = (payCodeId: number) => {
        let payCode = '';
        payCodeDropdownOptions.map(item => {
            if (item.id === payCodeId)
                payCode = item.name.toString();
        })
        updateAssetLoad(
            ['payCode'],
            [payCode]
        );
    }

    const handleOnChangeMiles = (value: string) => {
        if (!isEditLoadEnabled) return;

        const miles = parseInt(value);
        if (miles < 0) return;
        if (Number.isNaN(miles) || !Number.isInteger(miles))
            updateAssetLoad(['miles'], [null]);
        else
            updateAssetLoad(['miles'], [miles]);
    }

    const handleOnChangeAccessorialCost = (value: string) => {
        if (!isEditLoadEnabled) return;

        const cost = parseInt(value);
        if (cost < 0) return;
        if (Number.isNaN(cost) || !Number.isInteger(cost))
            updateAssetLoad(['accessorialCost'], [null]);
        else
            updateAssetLoad(['accessorialCost'], [cost]);
    }

    const handleOnChangeDriver = (driverId: number | null) => {
        if (!isAssignLoadEnabled) return;

        let driverName = '';
        if (driverId === 0) driverId = null;
        driverDropdownOptions.map(item => {
            if (item.id === driverId)
                driverName = item.name.toString();
        })
        let trailerId: number | null = null;
        let trailer: string = '';
        drivers?.getDriversByRegion?.map(item => {
            if (item?.id === driverId) {
                trailerId = item.trailerId ?? null;
                trailer = item.trailerDescrip ?? '';
            }
        })
        updateAssetLoad(['driverId', 'driverName', 'trailerId', 'trailer'], [driverId, driverName, trailerId, trailer]);
    }

    const handleOnChangeTruckNumber = (truckNumber: string) => {
        if (!isAssignLoadEnabled) return;

        if (truckNumber)
            updateAssetLoad(['truckNumber'], [truckNumber]);
        else
            updateAssetLoad(['truckNumber'], [null]);
    }

    const handleOnChangeTrailer = (trailerId: number | null) => {
        if (!isAssignLoadEnabled) return;

        let trailer = '';
        if (trailerId === 0) trailerId = null;
        trailerDropdownOptions.map(item => {
            if (item.id === trailerId)
                trailer = item.name.toString();
        })
        updateAssetLoad(['trailerId', 'trailer'], [trailerId, trailer]);
    }

    const isTrailerInvalid = () => {
        let isTrailerInvalid = true;
        if (!assetLoadToSave?.driverName)
            isTrailerInvalid = false;
        if (assetLoadToSave?.trailer === selectedTrailer)
            isTrailerInvalid = false;
        else
            trailerDropdownOptions.map(item => {
                if (assetLoadToSave?.trailer === item.name) isTrailerInvalid = false;
            })
        return isTrailerInvalid;
    }

    return (
        <>
            <div className='EditEquipmentDetails-Container'>
                <div className='EditEquipmentDetails-Header'>
                    Load Details
                </div>

                <div className='EditEquipmentDetails-Body'>
                    <Row>
                        <Col md={3} className='EditEquipmentDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={loadTypeDropdownOptions}
                                id="loadType"
                                label="LOAD TYPE"
                                onSelect={value => { value && handleOnChangeLoadType(parseInt(value)) }}
                                value={assetLoadToSave?.assetLoadType}
                                required
                                disabled={!isEditLoadEnabled}
                            />
                        </Col>
                        <Col md={3} className='EditEquipmentDetails-Field'>
                            <div className="Textfield-InputTitle input-group-text EditEquipmentDetails-InputNumber-Container">
                                *LOAD STATUS
                            </div>
                            <input
                                type="text"
                                title="loadStatus"
                                className="EditEquipmentDetails-Number EditEquipmentDetails-InputNumber Textfield-Disabled"
                                value={assetLoadToSave?.status ?? ''}
                                disabled
                                onChange={element => { }}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col md={3} className='EditEquipmentDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={originDestinationTypeDropdownOptions}
                                id="originType"
                                label="ORIGIN TYPE"
                                onSelect={value => { value && handleOnChangeTerminalType(parseInt(value), true) }}
                                value={assetLoadToSave?.originType}
                                required
                                disabled={assetInfoExists || !isEditLoadEnabled}
                            />
                        </Col>
                        {assetLoadToSave?.originType === 'Other' ? (
                            <>
                                <Col md={2} className='EditEquipmentDetails-Field'>
                                    <div className="Textfield-InputTitle input-group-text EditEquipmentDetails-InputNumber-Container">
                                        {assetLoadToSave.originLatitude || assetLoadToSave.originLongitude ? '*LATITUDE' : 'LATITUDE'}
                                    </div>
                                    <input
                                        type="number"
                                        title="latitude"
                                        className="EditEquipmentDetails-Number EditEquipmentDetails-InputNumber"
                                        value={assetLoadToSave?.originLatitude ?? ''}
                                        onChange={element => { handleOnChangeOriginLat(element.target.value) }}
                                        onKeyDown={e => ['e', 'E'].includes(e.key) && e.preventDefault()}
                                        disabled={assetInfoExists || !isEditLoadEnabled}
                                    />
                                    {isOriginLatError && <span className="EditEquipment-CoordsError">{latErrorMessage}</span>}
                                </Col>
                                <Col md={2} className='EditEquipmentDetails-Field'>
                                    <div className="Textfield-InputTitle input-group-text EditEquipmentDetails-InputNumber-Container">
                                        {assetLoadToSave.originLatitude || assetLoadToSave.originLongitude ? '*LONGITUDE' : 'LONGITUDE'}
                                    </div>
                                    <input
                                        type="number"
                                        title="longitude"
                                        className="EditEquipmentDetails-Number EditEquipmentDetails-InputNumber"
                                        value={assetLoadToSave?.originLongitude ?? ''}
                                        onChange={element => { handleOnChangeOriginLong(element.target.value) }}
                                        onKeyDown={e => ['e', 'E'].includes(e.key) && e.preventDefault()}
                                        disabled={assetInfoExists || !isEditLoadEnabled}
                                    />
                                    {isOriginLngError && <span className="EditEquipment-CoordsError">{lngErrorMessage}</span>}
                                </Col>
                            </>
                        ) : (
                            <Col md={3} className='EditEquipmentDetails-Field'>
                                <SearchableDropdown
                                    dropdownOptions={originDropdownOptions}
                                    id="origin"
                                    label="ORIGIN"
                                    onSelect={value => { value && handleOnChangeTerminal(parseInt(value), true) }}
                                    value={assetLoadToSave?.originJob ?? assetLoadToSave?.originLofa ?? assetLoadToSave?.originYard ?? ' '}
                                    required
                                    autoCompleteEnabled
                                    endIcon={searchIcon}
                                    disabled={!assetLoadToSave?.originType || assetInfoExists || !isEditLoadEnabled}
                                />
                            </Col>
                        )}
                        <Col md={assetLoadToSave?.originType === 'Other' ? 2 : 3} className='EditEquipmentDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={regionDropdownOptions}
                                id="originRegion"
                                label="ORIGIN REGION"
                                onSelect={value => { value && handleOnChangeRegion(parseInt(value), true) }}
                                value={assetLoadToSave?.originRegion}
                                required
                                disabled={assetInfoExists || !isEditLoadEnabled}
                            />
                        </Col>
                        <Col md={3} className='EditEquipmentDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={originDistrictDropdownOptions}
                                id="originDistrict"
                                label="ORIGIN DISTRICT"
                                onSelect={value => { value && handleOnChangeDistrict(parseInt(value), true) }}
                                value={assetLoadToSave?.originDistrict}
                                disabled={!assetLoadToSave?.originRegionId || assetInfoExists || !isEditLoadEnabled}
                                required
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col md={3} className='EditEquipmentDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={originDestinationTypeDropdownOptions}
                                id="destinationType"
                                label="DESTINATION TYPE"
                                onSelect={value => { value && handleOnChangeTerminalType(parseInt(value), false) }}
                                value={assetLoadToSave?.destinationType}
                                required
                                disabled={!isEditLoadEnabled}
                            />
                        </Col>
                        {assetLoadToSave?.destinationType === 'Other' ? (
                            <>
                                <Col md={2} className='EditEquipmentDetails-Field'>
                                    <div className="Textfield-InputTitle input-group-text EditEquipmentDetails-InputNumber-Container">
                                        *LATITUDE
                                    </div>
                                    <input
                                        type="number"
                                        title="latitude"
                                        className="EditEquipmentDetails-Number EditEquipmentDetails-InputNumber"
                                        value={assetLoadToSave?.destinationLatitude ?? ''}
                                        onChange={element => { handleOnChangeDestinationLat(element.target.value) }}
                                        onKeyDown={e => ['e', 'E'].includes(e.key) && e.preventDefault()}
                                        disabled={!isEditLoadEnabled}
                                    />
                                    {isDestLatError && <span className="EditEquipment-CoordsError">{latErrorMessage}</span>}
                                </Col>
                                <Col md={2} className='EditEquipmentDetails-Field'>
                                    <div className="Textfield-InputTitle input-group-text EditEquipmentDetails-InputNumber-Container">
                                        *LONGITUDE
                                    </div>
                                    <input
                                        type="number"
                                        title="longitude"
                                        className="EditEquipmentDetails-Number EditEquipmentDetails-InputNumber"
                                        value={assetLoadToSave?.destinationLongitude ?? ''}
                                        onChange={element => { handleOnChangeDestinationLong(element.target.value) }}
                                        onKeyDown={e => ['e', 'E'].includes(e.key) && e.preventDefault()}
                                        disabled={!isEditLoadEnabled}
                                    />
                                    {isDestLngError && <span className="EditEquipment-CoordsError">{lngErrorMessage}</span>}
                                </Col>
                            </>
                        ) : (
                            <Col md={3} className='EditEquipmentDetails-Field'>
                                <SearchableDropdown
                                    dropdownOptions={destinationDropdownOptions}
                                    id="destination"
                                    label="DESTINATION"
                                    onSelect={value => { value && handleOnChangeTerminal(parseInt(value), false) }}
                                    value={assetLoadToSave?.destinationJob ?? assetLoadToSave?.destinationLofa ?? assetLoadToSave?.destinationYard ?? ' '}
                                    required
                                    autoCompleteEnabled
                                    endIcon={searchIcon}
                                    disabled={!assetLoadToSave?.destinationType || !isEditLoadEnabled}
                                />
                            </Col>
                        )}
                        <Col md={assetLoadToSave?.destinationType === "Other" ? 2 : 3} className='EditEquipmentDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={regionDropdownOptions}
                                id="destinationRegion"
                                label="DESTINATION REGION"
                                onSelect={value => { value && handleOnChangeRegion(parseInt(value), false); }}
                                value={assetLoadToSave?.destinationRegion}
                                disabled={!isEditLoadEnabled}
                                required
                            />
                        </Col>
                        <Col md={3} className='EditEquipmentDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={destinationDistrictDropdownOptions}
                                id="destinationDistrict"
                                label="DESTINATION DISTRICT"
                                onSelect={value => { value && handleOnChangeDistrict(parseInt(value), false) }}
                                value={assetLoadToSave?.destinationDistrict}
                                required
                                disabled={!assetLoadToSave?.destinationRegionId || !isEditLoadEnabled}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col md={3} className='EditEquipmentDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={trailerTypeDropdownOptions}
                                id="trailerType"
                                label="TRAILER TYPE"
                                onSelect={value => { value && handleOnChangeTrailerType(parseInt(value)) }}
                                value={assetLoadToSave?.trailerType}
                                autoCompleteEnabled
                                endIcon={searchIcon}
                                required
                                disabled={!isEditLoadEnabled}
                            />
                        </Col>
                        <Col md={3} className='EditEquipmentDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={payCodeDropdownOptions}
                                id="payCode"
                                label="PAY CODE"
                                onSelect={value => { value && handleOnChangePayCode(parseInt(value)) }}
                                value={assetLoadToSave?.payCode}
                                disabled={!isEditLoadEnabled}
                            />
                        </Col>
                        <Col md={3} className='EditEquipmentDetails-Field'>
                            <div className="Textfield-InputTitle input-group-text EditEquipmentDetails-InputNumber-Container">
                                {assetLoadToSave?.trailerTypeId === 6 ? "*MILES" : "MILES"}
                            </div>
                            <input
                                type="number"
                                title="miles"
                                className="EditEquipmentDetails-Number EditEquipmentDetails-InputNumber"
                                value={assetLoadToSave?.miles ?? ''}
                                onChange={element => { handleOnChangeMiles(element.target.value) }}
                                onKeyDown={blockInvalidChar}
                                disabled={!isEditLoadEnabled}
                                onWheel={event => event.currentTarget.blur()}
                            />
                        </Col>
                        <Col md={3} className='EditEquipmentDetails-Field'>
                            <div className="Textfield-InputTitle input-group-text EditEquipmentDetails-InputNumber-Container">
                                ACCESSORIAL COST
                            </div>
                            <input
                                type="number"
                                title="accessorialCost"
                                className="EditEquipmentDetails-Number EditEquipmentDetails-InputNumber"
                                value={assetLoadToSave?.accessorialCost ?? 'x'}
                                onChange={element => { handleOnChangeAccessorialCost(element.target.value) }}
                                onKeyDown={blockInvalidChar}
                                disabled={!isEditLoadEnabled}
                                onWheel={event => event.currentTarget.blur()}
                            />
                        </Col>
                    </Row>
                </div>
            </div>

            <div className='EditEquipmentDetails-Container'>
                <div className='EditEquipmentDetails-Header'>
                    Driver Details
                </div>

                <div className='EditEquipmentDetails-Body'>
                    <Row>
                        <Col md={3} className='EditEquipmentDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={driverDropdownOptions}
                                id="driver"
                                label="DRIVER"
                                onSelect={value => { handleOnChangeDriver(parseInt(value ?? '')) }}
                                value={assetLoadToSave?.driverName}
                                autoCompleteEnabled
                                endIcon={searchIcon}
                                disabled={!isAssignLoadEnabled}
                            />
                        </Col>
                        <Col md={3} className='EditEquipmentDetails-Field'>
                            <div className="Textfield-InputTitle input-group-text EditEquipmentDetails-InputNumber-Container">
                                TRUCK ID
                            </div>
                            <input
                                type="text"
                                title="truckId"
                                className="EditEquipmentDetails-Number EditEquipmentDetails-InputNumber"
                                value={assetLoadToSave?.truckNumber ?? ''}
                                onChange={element => { handleOnChangeTruckNumber(element.target.value ?? '') }}
                                disabled={!isAssignLoadEnabled}
                            />
                        </Col>
                        <Col md={3} className='EditEquipmentDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={trailerDropdownOptions}
                                id="trailer"
                                label="TRAILER ID"
                                onSelect={value => { value && handleOnChangeTrailer(parseInt(value)) }}
                                value={assetLoadToSave?.trailer ?? ' '}
                                autoCompleteEnabled
                                endIcon={searchIcon}
                                disabled={!isAssignLoadEnabled || !assetLoadToSave?.trailerTypeId || trailerDataLoading}
                                required={assetLoadToSave?.driverName ? true : false}
                                hasError={isTrailerInvalid()}
                                errorMessage='The trailer id does not match this loads trailer type'
                            />
                        </Col>
                    </Row>

                </div>
            </div>
        </>
    )
}

export default LoadDriverDetailsEdit;